import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

import "./stylePermisos.css";

const API = process.env.REACT_APP_BASEURL;

const ModuloPermisos = () => {
  // ============================================================
  // ESTADOS
  // ============================================================

  const [permisos, setPermisos] = useState([]);
  const [busqueda, setBusqueda] = useState("");

  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Formulario de creación
  const [formulario, setFormulario] = useState({
    clave: "",
    descripcion: "",
    activo: true,
  });

  // ============================================================
  // NORMALIZAR ESTADO
  // ============================================================

  const esActivo = (valor) => {
    return (
      valor === true ||
      valor === 1 ||
      valor === "1" ||
      valor === "true"
    );
  };

  // ============================================================
  // CARGAR PERMISOS
  // ============================================================

  const cargarPermisos = async () => {
    try {
      setCargando(true);

      const response = await axios.post(`${API}permisos`);

      if (response.data?.ok) {
        setPermisos(response.data.data || []);
      } else {
        setPermisos([]);
      }
    } catch (error) {
      console.error("Error cargando permisos:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.mensaje ||
          error?.response?.data?.message ||
          "No se pudieron cargar los permisos.",
      });
    } finally {
      setCargando(false);
    }
  };

  // ============================================================
  // CARGAR AL INICIAR
  // ============================================================

  useEffect(() => {
    cargarPermisos();
  }, []);

  // ============================================================
  // CAMBIOS DEL FORMULARIO
  // ============================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormulario((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ============================================================
  // LIMPIAR FORMULARIO
  // ============================================================

  const limpiarFormulario = () => {
    setFormulario({
      clave: "",
      descripcion: "",
      activo: true,
    });
  };

  // ============================================================
  // CREAR PERMISO
  // ============================================================

  const crearPermiso = async (e) => {
    e.preventDefault();

    const clave = formulario.clave.trim();
    const descripcion = formulario.descripcion.trim();

    if (!clave) {
      Swal.fire({
        icon: "warning",
        title: "Dato requerido",
        text: "La clave del permiso es obligatoria.",
      });

      return;
    }

    try {
      setGuardando(true);

      const response = await axios.post(`${API}permisos/crea`, {
        clave,
        descripcion,
        activo: true,
      });

      if (response.data?.ok) {
        limpiarFormulario();

        await cargarPermisos();

        await Swal.fire({
          icon: "success",
          title: "Permiso creado",
          text: "El permiso fue creado correctamente.",
          timer: 1800,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "No se pudo crear",
          text:
            response.data?.mensaje ||
            "No se pudo crear el permiso.",
        });
      }
    } catch (error) {
      console.error("Error creando permiso:", error);

      Swal.fire({
        icon: "error",
        title: "No se pudo crear",
        text:
          error?.response?.data?.mensaje ||
          error?.response?.data?.message ||
          "Ocurrió un error al crear el permiso.",
      });
    } finally {
      setGuardando(false);
    }
  };

  // ============================================================
  // EDITAR PERMISO
  // ============================================================

  const editarPermiso = async (permiso) => {
    const { value: formularioEditar } = await Swal.fire({
      title: "✏️ Editar permiso",

      html: `
        <div class="swal-permiso-form">

          <div class="swal-permiso-grupo">
            <label for="swal-clave">
              Clave del permiso
            </label>

            <input
              id="swal-clave"
              class="swal2-input"
              type="text"
              value="${String(permiso.clave || "")
                .replace(/"/g, "&quot;")}"
              maxlength="100"
              placeholder="Clave del permiso"
            />
          </div>

          <div class="swal-permiso-grupo">
            <label for="swal-descripcion">
              Descripción
            </label>

            <textarea
              id="swal-descripcion"
              class="swal2-textarea"
              maxlength="255"
              placeholder="Descripción del permiso"
            >${String(permiso.descripcion || "")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")}</textarea>
          </div>

          <div class="swal-permiso-estado">
            <span>Estado actual:</span>

            <strong class="${
              esActivo(permiso.activo)
                ? "estado-activo"
                : "estado-inactivo"
            }">
              ${
                esActivo(permiso.activo)
                  ? "● Activo"
                  : "● Inactivo"
              }
            </strong>
          </div>

        </div>
      `,

      showCancelButton: true,

      confirmButtonText: "💾 Guardar cambios",
      cancelButtonText: "Cancelar",

      confirmButtonColor: "#0f766e",
      cancelButtonColor: "#64748b",

      width: "520px",

      focusConfirm: false,

      allowOutsideClick: () => !Swal.isLoading(),

      preConfirm: () => {
        const clave =
          document
            .getElementById("swal-clave")
            ?.value.trim();

        const descripcion =
          document
            .getElementById("swal-descripcion")
            ?.value.trim();

        if (!clave) {
          Swal.showValidationMessage(
            "La clave del permiso es obligatoria."
          );

          return false;
        }

        return {
          clave,
          descripcion,
        };
      },
    });

    // Si presionó Cancelar
    if (!formularioEditar) {
      return;
    }

    try {
      setGuardando(true);

      Swal.fire({
        title: "Guardando cambios...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

     const response = await axios.put(
  `${API}permisos/actualiza/${permiso.id}`,
  {
    clave: formularioEditar.clave,
    descripcion: formularioEditar.descripcion,
    activo: esActivo(permiso.activo),
  }
);
      if (response.data?.ok) {
        await cargarPermisos();

        await Swal.fire({
          icon: "success",
          title: "¡Permiso actualizado!",
          text: "Los cambios fueron guardados correctamente.",
          timer: 1800,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "No se pudo actualizar",
          text:
            response.data?.mensaje ||
            "No se pudo actualizar el permiso.",
        });
      }
    } catch (error) {
      console.error("Error actualizando permiso:", error);

      Swal.fire({
        icon: "error",
        title: "No se pudo actualizar",
        text:
          error?.response?.data?.mensaje ||
          error?.response?.data?.message ||
          "Ocurrió un error al actualizar el permiso.",
      });
    } finally {
      setGuardando(false);
    }
  };

  // ============================================================
  // CAMBIAR ESTADO
  // ============================================================

  const cambiarEstado = async (permiso) => {
    const estaActivo = esActivo(permiso.activo);

    const nuevoEstado = !estaActivo;

    const result = await Swal.fire({
      icon: nuevoEstado ? "question" : "warning",

      title: nuevoEstado
        ? "¿Activar permiso?"
        : "¿Desactivar permiso?",

      html: `
        <div>
          <p>
            Vas a ${
              nuevoEstado
                ? "activar"
                : "desactivar"
            } el siguiente permiso:
          </p>

          <strong>${permiso.clave}</strong>

          ${
            permiso.descripcion
              ? `
                <p class="swal-descripcion">
                  ${permiso.descripcion}
                </p>
              `
              : ""
          }
        </div>
      `,

      showCancelButton: true,

      confirmButtonText: nuevoEstado
        ? "Sí, activar"
        : "Sí, desactivar",

      cancelButtonText: "Cancelar",

      confirmButtonColor: nuevoEstado
        ? "#198754"
        : "#dc3545",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      Swal.fire({
        title: nuevoEstado
          ? "Activando permiso..."
          : "Desactivando permiso...",

        allowOutsideClick: false,
        allowEscapeKey: false,

        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await axios.put(
        `${API}permisos/estado/${permiso.id}`,
        {
          activo: nuevoEstado,
        }
      );

      if (response.data?.ok) {
        Swal.close();

        await cargarPermisos();

        await Swal.fire({
          icon: "success",

          title: nuevoEstado
            ? "Permiso activado"
            : "Permiso desactivado",

          text: nuevoEstado
            ? "El permiso está nuevamente activo."
            : "El permiso fue desactivado correctamente.",

          timer: 1600,
          showConfirmButton: false,
        });
      } else {
        Swal.close();

        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            response.data?.mensaje ||
            "No se pudo cambiar el estado del permiso.",
        });
      }
    } catch (error) {
      console.error(
        "Error cambiando estado:",
        error
      );

      Swal.close();

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.mensaje ||
          error?.response?.data?.message ||
          "No se pudo cambiar el estado del permiso.",
      });
    }
  };

  // ============================================================
  // FILTRO
  // ============================================================

  const permisosFiltrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();

    if (!texto) {
      return permisos;
    }

    return permisos.filter((permiso) => {
      const clave = String(
        permiso.clave || ""
      ).toLowerCase();

      const descripcion = String(
        permiso.descripcion || ""
      ).toLowerCase();

      return (
        clave.includes(texto) ||
        descripcion.includes(texto)
      );
    });
  }, [permisos, busqueda]);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="permisos-container">

      {/* ======================================================
          ENCABEZADO
      ====================================================== */}

      <div className="permisos-header">

        <div>
          <h2>🔐 Gestión de Permisos</h2>

          <p>
            Administración de los permisos utilizados por
            SIGEDI.
          </p>
        </div>

        <button
          type="button"
          className="permisos-btn-recargar"
          onClick={cargarPermisos}
          disabled={cargando}
        >
          🔄 Recargar
        </button>

      </div>


      {/* ======================================================
          FORMULARIO CREAR
      ====================================================== */}

      <div className="permisos-card">

        <div className="permisos-card-header">

          <h3>
            ➕ Crear permiso
          </h3>

        </div>

        <form
          className="permisos-form"
          onSubmit={crearPermiso}
        >

          <div className="permisos-form-grupo">

            <label htmlFor="clave">
              Clave del permiso
            </label>

            <input
              id="clave"
              name="clave"
              type="text"
              value={formulario.clave}
              onChange={handleChange}
              placeholder="Ej: Gestiona Usuarios"
              maxLength={100}
              autoComplete="off"
              disabled={guardando}
            />

          </div>


          <div className="permisos-form-grupo">

            <label htmlFor="descripcion">
              Descripción
            </label>

            <input
              id="descripcion"
              name="descripcion"
              type="text"
              value={formulario.descripcion}
              onChange={handleChange}
              placeholder="Descripción del permiso"
              maxLength={255}
              disabled={guardando}
            />

          </div>


          <div className="permisos-form-acciones">

            <button
              type="submit"
              className="permisos-btn guardar"
              disabled={guardando}
            >
              {guardando
                ? "Guardando..."
                : "➕ Crear permiso"}
            </button>

            <button
              type="button"
              className="permisos-btn cancelar"
              onClick={limpiarFormulario}
              disabled={guardando}
            >
              Limpiar
            </button>

          </div>

        </form>

      </div>


      {/* ======================================================
          BUSCADOR
      ====================================================== */}

      <div className="permisos-card permisos-buscador">

        <div className="permisos-buscador-header">

          <div>

            <h3>
              🔎 Buscar permisos
            </h3>

            <span>
              {permisosFiltrados.length} de{" "}
              {permisos.length} permisos
            </span>

          </div>

          {busqueda && (
            <button
              type="button"
              className="permisos-limpiar-busqueda"
              onClick={() => setBusqueda("")}
            >
              ✖ Limpiar
            </button>
          )}

        </div>


        <input
          type="text"
          value={busqueda}
          onChange={(e) =>
            setBusqueda(e.target.value)
          }
          placeholder="Buscar por clave o descripción..."
          autoComplete="off"
        />

      </div>


      {/* ======================================================
          TABLA
      ====================================================== */}

      <div className="permisos-card">

        <div className="permisos-card-header">

          <h3>
            📋 Permisos registrados
          </h3>

        </div>


        {cargando ? (

          <div className="permisos-cargando">

            <div className="permisos-spinner"></div>

            <p>
              Cargando permisos...
            </p>

          </div>

        ) : permisosFiltrados.length === 0 ? (

          <div className="permisos-vacio">

            <span>
              🔐
            </span>

            <h4>
              No se encontraron permisos
            </h4>

            <p>
              {busqueda
                ? "No hay permisos que coincidan con la búsqueda."
                : "Todavía no hay permisos registrados."}
            </p>

          </div>

        ) : (

          <div className="permisos-tabla-wrapper">

            <table className="permisos-tabla">

              <thead>

                <tr>

                  <th>ID</th>

                  <th>Clave</th>

                  <th>Descripción</th>

                  <th>Estado</th>

                  <th>Acciones</th>

                </tr>

              </thead>


              <tbody>

                {permisosFiltrados.map((permiso) => {

                  const activo =
                    esActivo(permiso.activo);

                  return (

                    <tr
                      key={permiso.id}
                      className={
                        !activo
                          ? "permiso-inactivo"
                          : ""
                      }
                    >

                      <td>

                        <span className="permiso-id">
                          {permiso.id}
                        </span>

                      </td>


                      <td>

                        <strong className="permiso-clave">
                          {permiso.clave}
                        </strong>

                      </td>


                      <td>

                        <span className="permiso-descripcion">

                          {permiso.descripcion ||
                            "Sin descripción"}

                        </span>

                      </td>


                      <td>

                        <span
                          className={`permiso-estado ${
                            activo
                              ? "activo"
                              : "inactivo"
                          }`}
                        >

                          {activo
                            ? "● Activo"
                            : "● Inactivo"}

                        </span>

                      </td>


                      <td>

                        <div className="permiso-acciones">

                          {/* EDITAR */}

                          <button
                            type="button"
                            className="permiso-accion editar"
                            onClick={() =>
                              editarPermiso(permiso)
                            }
                            title="Editar permiso"
                          >
                            ✏️
                          </button>


                          {/* ACTIVAR / DESACTIVAR */}

                          <button
                            type="button"
                            className={`permiso-accion ${
                              activo
                                ? "desactivar"
                                : "activar"
                            }`}
                            onClick={() =>
                              cambiarEstado(permiso)
                            }
                            title={
                              activo
                                ? "Desactivar permiso"
                                : "Activar permiso"
                            }
                          >
                            {activo
                              ? "🔴"
                              : "🟢"}
                          </button>

                        </div>

                      </td>

                    </tr>

                  );

                })}

              </tbody>

            </table>

          </div>

        )}

      </div>

    </div>
  );
};

export default ModuloPermisos;